import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Dictionary, IBaseEntity, UserPreferencesSettingsUpdate } from '../../../entities/common';
import { ApplicationState } from '../../../store';
import { actionCreators as SectionsActionCreators } from '../../../store/Sections';
import { useDidMount } from '../../utils/effects';

type BaseComponentProps = { entity: IBaseEntity; } & SectionsSettingsProps;

export type OnSaveSettings = (update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => void;

type ActionProps = {
    sectionsActions: typeof SectionsActionCreators;
}

type StateProps = {
    isLoading: boolean;
    sectionsSettings?: Dictionary<any>;
}

export type SectionsSettingsProps<T = Dictionary<any>> = {
    sectionsSettings?: T;
    onSaveSettings?: OnSaveSettings;
}

export const withSectionsSettings = <P extends BaseComponentProps>(WrappedComponent: React.ComponentType<P>) => {

    type HOCOwnProps = Omit<P, keyof SectionsSettingsProps>

    const mapDispatchToProps = (dispatch: Dispatch, ownProps: HOCOwnProps): ActionProps => ({
        sectionsActions: bindActionCreators(SectionsActionCreators, dispatch)
    });

    const mapStateToProps = (state: ApplicationState, ownProps: HOCOwnProps): StateProps => ({
        isLoading: ownProps?.entity.id ? state.sections.isLoading[ownProps.entity.id] : true,
        sectionsSettings: state.sections.settings
    });

    const Component = (props: StateProps & ActionProps & HOCOwnProps) => {
        const didMount = useDidMount();

        React.useEffect(() => {
            props.sectionsActions.loadSettings(props.entity.id);
        }, [props.entity.id])

        const onSaveSetting = React.useCallback((update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => {
            const updateWithEntityId: UserPreferencesSettingsUpdate = {
                parentSettingsPathKeys: [props.entity.id, ...update.parentSettingsPathKeys],
                valueBySettingNameMap: update.valueBySettingNameMap
            };

            props.sectionsActions.saveSettings(updateWithEntityId, sendToServer ?? true);
        }, [props.entity.id]);

        return <WrappedComponent
            {...props as unknown as P}
            onSaveSettings={(props.isLoading || !didMount) ? undefined : onSaveSetting}
            sectionsSettings={props.sectionsSettings?.[props.entity.id]} />
    }

    return connect(mapStateToProps, mapDispatchToProps)(Component as any) as React.ComponentType<HOCOwnProps>;
}